import {
  Alert,
  Button,
  Chip,
  Divider,
  FormControl,
  FormHelperText,
  ListItemText,
  MenuItem,
  MenuList,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Box, useMediaQuery } from "@mui/system";
import { FormikErrors, FormikProps, getIn } from "formik";
import { isNil, orderBy } from "lodash-es";
import { useEffect, useMemo, useState } from "react";

import AppDropdownIconButton from "@/common/components/Button/AppDropdownIconButton";
import AppIconButton from "@/common/components/Button/AppIconButton";
import { DevRenderCount } from "@/common/components/Dev/DevRenderCount";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { EntityHelper } from "@/common/helpers/entity";
import { DamageCostEvaluationFormPermissions } from "@/common/helpers/entity/damageCostEvaluation";
import { RepairHelper } from "@/common/helpers/entity/repair";
import { FormikHelper } from "@/common/helpers/formik";
import { IdHelper } from "@/common/helpers/id";
import { MemoHelper, memoWithReversedArgs } from "@/common/helpers/memo";
import { TypeHelper } from "@/common/helpers/type";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { Mapper } from "@/common/mapping/mapper";
import { VehicleDamageDataInputDtoLocal } from "@/common/mapping/mappers/vehicleDamage";
import { cast } from "@/common/ts/conversions";
import { EntityInputFormInfo } from "@/common/ts/entity";
import { ValidationHelper } from "@/common/validation";
import {
  AppPermission,
  EntityDataOriginMode,
  GeneralCurrencyInputDto,
  RepairSpecDataDto,
  RepairSpecDataInputDto,
  RepairSpecDetalizationType,
  RepairSpecDto,
  RepairSpecMatchQueryDto,
  VehicleReferenceDto,
  VehicleType,
} from "@/core/api/generated/v1";

import NoDataAlert from "../../../AppAlerts/NoDataAlert";
import AuthorizedElement from "../../../Auth/AuthorizedElement";
import FoldableBlock from "../../../Display/FoldableBlock";
import FileUploader from "../../../Files/FileUploader";
import AppTextArea from "../../../Form/Input/AppTextArea";
import AppIcon from "../../../Icons/AppIcon";
import CompoundIcon from "../../../Icons/CompoundIcon";
import GeneralPriceSummaryDisplay from "../../../PriceSummary/GeneralPriceSummaryDisplay";
import RepairSpecCreateUpdateModal from "../../RepairSpec/CreateUpdate/RepairSpecCreateUpdateModal";
import RepairSpecDataInputDtoFormInputs from "../../RepairSpec/CreateUpdate/RepairSpecDataInputDtoFormInputs";
import SelectOrCreateRepairSpec from "../../RepairSpec/SelectOrCreateRepairSpec";
import VehicleDamageAutocomplete from "../../VehicleDamage/VehicleDamageAutocomplete";
import VehicleDamageInline from "../../VehicleDamage/VehicleDamageInline";
import { VehicleDamageDataViewModal } from "../../VehicleDamage/View/VehicleDamageDataViewModal";
import CurrencyMismatchWithRepairSpecAlert from "../CurrencyMismatchWithRepairSpecAlert";
import {
  DamageCostEvaluationFormSettings,
  DamageCostEvaluationItemDamageValues,
  DamageCostEvaluationItemValues,
  DamageCostEvaluationValues,
} from "../DamageCostEvaluationCreateUpdate";

export interface DamageCostEvaluationItemInputProps {
  formInfo: EntityInputFormInfo;
  formPerms: DamageCostEvaluationFormPermissions;
  formSettings: DamageCostEvaluationFormSettings;
  formikProps: FormikProps<DamageCostEvaluationValues>;
  vehicleId: string;
  vehicle: VehicleReferenceDto | undefined;
  currency: GeneralCurrencyInputDto | undefined;
  selectedDamageIds: Nil<string[]>;
  itemCount: number; // ensures that all items will re-render if any item is added/removed
  item: DamageCostEvaluationItemValues;
  itemIndex: number;
  isPrefillRepairSpec: boolean;
  /** When clicked update on inline damage data. */
  onUpdateInlineDamageData: (params: {
    item: DamageCostEvaluationItemValues;
    itemDamage: DamageCostEvaluationItemDamageValues;
    damageData: VehicleDamageDataInputDtoLocal;
  }) => void;
}

export default memoWithReversedArgs(
  MemoHelper.arePropsEqual.factory<DamageCostEvaluationItemInputProps>({
    // debugKey: "DamageCostEvaluationItemInput",
    isExcludeFunctionProps: true,
    excludeProps: (t) => [t.formikProps.$path],
    includeProps: (t) => [t.formikProps.errors.$path],
  }),
  function DamageCostEvaluationItemInput({
    formInfo,
    formPerms,
    formSettings,
    formikProps,
    vehicleId,
    vehicle,
    currency,
    selectedDamageIds,
    itemCount,
    item,
    itemIndex,
    isPrefillRepairSpec,
    onUpdateInlineDamageData,
  }: DamageCostEvaluationItemInputProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const currentTenant = useCurrentTenant();

    const { values, errors, setFieldValue, handleBlur, handleChange, setFieldError } = formikProps;

    const [vehicleDamageDataToView, setVehicleDamageDataToView] =
      useState<Nil<VehicleDamageDataInputDtoLocal>>(null);
    const [isCreateRepairSpecModalOpen, setIsCreateRepairSpecModalOpen] = useState(false);
    const [createRepairSpecFromSpecData, setCreateRepairSpecFromSpecData] = useState<
      (RepairSpecDataDto & RepairSpecDataInputDto) | undefined
    >(undefined);

    // use match to query best suggestions (match by the first damage)
    const firstDamageData = useMemo(
      () => item.damages?.find((x) => !!x.damage)?.damage,
      [item.damages, item.damages?.at(0)?.damage],
    );
    const repairSpecMatchQuery: RepairSpecMatchQueryDto = {
      vehicleId: vehicleId || undefined,
      damageTypeId: firstDamageData?.damageTypeId || undefined,
      partTypeId: firstDamageData?.partTypeId || undefined,
    };

    // const timeoutRef = useRef<undefined | NodeJS.Timeout>(undefined);
    // useEffect(() => {
    //   // cleanup timeout on unmount
    //   return () => {
    //     if (timeoutRef.current) {
    //       clearTimeout(timeoutRef.current);
    //       timeoutRef.current = undefined;
    //     }
    //   };
    // }, []);

    const setItemNameAuto = (newValue: Nil<string>) => {
      if ((!item.isNameEditedManually || !item.name) && newValue) {
        setFieldValue(`items[${itemIndex}].name`, newValue);
      }
    };

    const setItemRepairSpec = (
      newValue: Nil<RepairSpecDto>,
      options?: { isExternal?: boolean },
    ) => {
      const isExternal = RepairHelper.isExternal({
        tenantId: currentTenant?.id,
        repairEntity: newValue,
      });
      setFieldValue(
        `items[${itemIndex}].repairSpec`,
        newValue?.id ? Mapper.repairSpec.mapToDataInputDto(newValue) : undefined,
      );
      setFieldValue(`items[${itemIndex}].isRepairSpecChanged`, isExternal ? true : false);
      setItemNameAuto(newValue?.name);
    };

    const [loadedEntities, setLoadedEntities] = useState<RepairSpecDto[] | null>(null);

    // Auto-select best matching repair spec using useEffect instead of setTimeout.
    // This approach eliminates race conditions and ensures the selection happens at the right moment:
    // - Triggers immediately when entities are actually loaded (not after arbitrary delay)
    // - Works consistently across all devices regardless of performance
    // - Properly integrates with React lifecycle and Formik's state management
    useEffect(() => {
      if (
        formInfo.isCreate &&
        isPrefillRepairSpec &&
        !item.repairSpec &&
        !item.isInitiallyAutoSelectedBestMatchingRepairSpec &&
        loadedEntities &&
        loadedEntities.length > 0
      ) {
        const newValue = orderBy(loadedEntities, (x) => x.matchMeta?.score ?? 0, "desc").at(0);

        if (newValue) {
          setItemRepairSpec(newValue);
          setFieldValue(`items[${itemIndex}].isInitiallyAutoSelectedBestMatchingRepairSpec`, true);
        }

        setLoadedEntities(null);
      }
    }, [loadedEntities, item.repairSpec, item.isInitiallyAutoSelectedBestMatchingRepairSpec]);

    return (
      <Box>
        <Stack spacing={2}>
          <DevRenderCount description='DamageCostEvaluationItemInput' />

          {/* Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 0fr",
              gridTemplateRows: "auto",
              gap: 2,
              alignItems: "start",
            }}
          >
            <Stack direction='row' spacing={2} sx={{ alignItems: "center" }}>
              <Typography variant='h3'>#{itemIndex + 1}</Typography>

              <TextField
                variant='outlined'
                size='small'
                label='Name'
                required
                disabled={!formPerms.canEdit}
                fullWidth
                error={Boolean(getIn(errors, `items[${itemIndex}].name`))}
                helperText={ValidationHelper.getFormikErrorsAsString(
                  getIn(errors, `items[${itemIndex}].name`),
                )}
                value={item.name || ""}
                onChange={(e) => {
                  setFieldValue(`items[${itemIndex}].name`, e.target.value || undefined);
                  setFieldValue(`items[${itemIndex}].isNameEditedManually`, true);
                  setFieldError(`items[${itemIndex}].name`, undefined);
                }}
              />
            </Stack>

            {/* Controls */}
            <Stack
              direction='row'
              sx={{
                ml: "auto",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              {/* Notes */}
              {(() => {
                const isAdd = isNil(item.notes);
                return (
                  <Tooltip title={isAdd ? "Add notes" : "Remove notes"}>
                    <AppIconButton
                      disabled={!formPerms.canEdit}
                      onClick={() =>
                        setFieldValue(`items[${itemIndex}].notes`, isAdd ? "" : undefined)
                      }
                    >
                      <CompoundIcon
                        mainIcon={<AppIcon of='notes' />}
                        badgeIcon={<AppIcon of={isAdd ? "add" : "remove"} />}
                      />
                    </AppIconButton>
                  </Tooltip>
                );
              })()}

              {/* Attachments */}
              {(() => {
                const isAdd = isNil(item.attachments);
                return (
                  <Tooltip title={isAdd ? "Add attachments" : "Remove attachments"}>
                    <AppIconButton
                      disabled={!formPerms.canEdit}
                      onClick={() =>
                        setFieldValue(`items[${itemIndex}].attachments`, isAdd ? [] : undefined)
                      }
                    >
                      <CompoundIcon
                        mainIcon={<AppIcon of='attachments' />}
                        badgeIcon={<AppIcon of={isAdd ? "add" : "remove"} />}
                      />
                    </AppIconButton>
                  </Tooltip>
                );
              })()}

              {/* Remove item */}
              <Tooltip title='Remove item'>
                <AppIconButton
                  disabled={!formPerms.canRemoveItems}
                  onClick={() => {
                    setFieldValue(
                      `items`,
                      values.items?.filter((x) => x.tempId !== item.tempId),
                    );
                  }}
                >
                  <AppIcon of='remove' />
                </AppIconButton>
              </Tooltip>
            </Stack>
          </Box>

          <Divider />

          {/* Currency mismatch alert */}
          {currency && <CurrencyMismatchWithRepairSpecAlert entity={values} item={item} />}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xxs: "1fr", md: "1fr 1fr" },
              gridTemplateRows: "auto",
              gap: 2,
              alignItems: "start",
            }}
          >
            {/* Damages */}
            <Stack spacing={2}>
              <Stack spacing={2}>
                {TypeHelper.isEmptyArray(item.damages) && (
                  <NoDataAlert title='No damages added' variant='inline' />
                )}

                {item.damages?.map((itemDamage, itemDamageIndex) => {
                  // prefer ref mode
                  const isRefDataMode = TypeHelper.isNilOrEqualTo(
                    itemDamage.damage && itemDamage.damage.dataOriginMode,
                    EntityDataOriginMode.Ref,
                  );
                  const isInlineDataMode =
                    itemDamage.damage &&
                    itemDamage.damage.dataOriginMode === EntityDataOriginMode.Inline;
                  const isAnyMeaningfulInlineData =
                    isInlineDataMode &&
                    itemDamage.damage &&
                    (itemDamage.damage.partTypeId || itemDamage.damage.damageTypeId);

                  // there is an edge case when values.selectedDamageIds is not computed yet
                  // but autocomplete already fetching data and won't refetch with latest params
                  // const excludeDamageIds = values.selectedDamageIds?.filter(
                  //   (id) => !!id && id !== itemDamage.damage?.id && !IdHelper.isTempFeId(id),
                  // );
                  const excludeDamageIds = selectedDamageIds?.filter(
                    (id) => !!id && id !== itemDamage.damage?.id && !IdHelper.isTempFeId(id),
                  );

                  const damageDataErrors = getIn(
                    errors,
                    `items[${itemIndex}].damages[${itemDamageIndex}].damage`,
                  ) as FormikErrors<typeof itemDamage.damage>;

                  return (
                    <Box
                      key={itemDamageIndex}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 0fr",
                        gridTemplateRows: "auto",
                        columnGap: 1,
                        rowGap: 1,
                      }}
                    >
                      {/* Damage */}
                      <FormControl fullWidth margin='none'>
                        {/* Ref data (existing damage) */}
                        {isRefDataMode && (
                          <VehicleDamageAutocomplete
                            entityId={IdHelper.nullIfTempFeId(itemDamage.damage?.id)}
                            requestConfig={
                              {
                                // skip: isNil(values.selectedDamageIds), // why this was here? it prevented loading options when no damages were selected yet
                              }
                            }
                            searchFilters={{
                              vehicleId: vehicleId || "",
                              excludeIds: excludeDamageIds,
                            }}
                            defaultCustomFilters={{
                              suggestedIds: formSettings.vehicleDamageIdsSuggested,
                              restrictedIds: formSettings.vehicleDamageIdsRestricted,
                            }}
                            disabled={
                              !formPerms.canEditDamages ||
                              TypeHelper.isNotNilAndNotEqualTo(
                                itemDamage.damage?.dataOriginMode,
                                EntityDataOriginMode.Ref,
                              )
                            }
                            required
                            fullWidth
                            isAutoSelectSingleOption={false}
                            size='small'
                            textFieldProps={{
                              error: Boolean(
                                getIn(
                                  errors,
                                  `items[${itemIndex}].damages[${itemDamageIndex}].damage`,
                                ),
                              ),
                              helperText: ValidationHelper.getFormikErrorsAsString(
                                getIn(
                                  errors,
                                  `items[${itemIndex}].damages[${itemDamageIndex}].damage`,
                                ),
                              ),
                            }}
                            shouldHide={(entity) =>
                              (!!formSettings.vehicleDamageIdsRestricted &&
                                !formSettings.vehicleDamageIdsRestricted.includes(
                                  entity.id || "",
                                )) ||
                              (!!excludeDamageIds && excludeDamageIds.includes(entity.id || ""))
                            }
                            onChange={(newValue) => {
                              const updatedItem = { ...item };

                              if (updatedItem.damages?.[itemDamageIndex]) {
                                let newDamageData: VehicleDamageDataInputDtoLocal | undefined =
                                  undefined;
                                if (newValue) {
                                  newDamageData = Mapper.vehicleDamage.mapToDataInputDto(newValue);
                                  newDamageData = EntityHelper.switchEntityDataToRefMode(
                                    newDamageData,
                                    newValue,
                                  );
                                }

                                updatedItem.damages[itemDamageIndex].damage =
                                  newDamageData || undefined;
                                updatedItem.damages[itemDamageIndex].damageLocal =
                                  newValue || undefined;

                                // setFieldValue(
                                //   "selectedDamageIds",
                                //   values.selectedDamageIds?.extensions
                                //     .filterIf(!!oldDamageId, (x) => x !== oldDamageId)
                                //     .extensions.concatItemIf(!!newDamageId, newDamageId || ""),
                                // );
                              }

                              // filtering attachments to keep only those
                              // that are actual and vehicleDamage attached
                              const computedAttachments = [
                                ...(updatedItem.attachments?.filter((prevAttachment) => {
                                  // if attachment have not id it means it is new attachment
                                  if (isNil(prevAttachment.id)) {
                                    return true;
                                  }
                                  return updatedItem?.damages?.some((prevDamage) =>
                                    prevDamage.damage?.attachments?.some(
                                      (att) => att.id === prevAttachment.id,
                                    ),
                                  );
                                }) || []),
                                ...(newValue?.attachments || []),
                              ];

                              updatedItem.attachments =
                                computedAttachments.length > 0 ? computedAttachments : undefined;

                              setFieldValue(`items[${itemIndex}]`, updatedItem);
                              setFieldError(
                                `items[${itemIndex}].damages[${itemDamageIndex}].damage`,
                                undefined,
                              );
                            }}
                          />
                        )}

                        {/* Inline data (local damage) */}
                        {isInlineDataMode && itemDamage.damage && (
                          <Box>
                            {!isAnyMeaningfulInlineData && (
                              // <AppTypography variant='body2'>No local data yet. Start editing to create it.</AppTypography>
                              <Alert severity='info'>
                                No local data yet. Start editing to create it.
                              </Alert>
                            )}

                            {isAnyMeaningfulInlineData && (
                              <Box>
                                <VehicleDamageInline
                                  entity={itemDamage.damage}
                                  entityId={undefined}
                                />{" "}
                                <Chip
                                  variant='outlined'
                                  size='small'
                                  color='default'
                                  label='Local'
                                />
                              </Box>
                            )}
                          </Box>
                        )}

                        {damageDataErrors && (
                          <FormControl margin='dense' fullWidth error>
                            <FormHelperText error>Invalid damage data.</FormHelperText>

                            <FormHelperText error>
                              {ValidationHelper.getFormikErrorsAsString(damageDataErrors, {
                                isIncludeNested: false,
                              })}
                            </FormHelperText>
                          </FormControl>
                        )}
                      </FormControl>

                      {/* Controls */}
                      <Stack
                        direction='row'
                        sx={{
                          ml: "auto",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        {/* Switch to inline mode */}
                        {isRefDataMode && (
                          <AppTooltip
                            title={`Edit as local copy. You'll edit your own damage copy. Original damage remains unchanged.`}
                          >
                            <AppIconButton
                              size='small'
                              color='text'
                              disabled={!formPerms.canEditDamages}
                              onClick={() => {
                                const newDamageData: typeof itemDamage.damage = itemDamage.damage
                                  ? EntityHelper.switchEntityDataToInlineMode(itemDamage.damage)
                                  : EntityHelper.newBaseEntityDataInInlineMode();
                                const newItemDamage: typeof itemDamage = {
                                  damage: newDamageData,
                                  damageLocal: undefined,
                                };
                                setFieldValue(
                                  `items[${itemIndex}].damages[${itemDamageIndex}]`,
                                  newItemDamage,
                                );
                              }}
                            >
                              <AppIcon of='swapHoriz' />
                            </AppIconButton>
                          </AppTooltip>
                        )}

                        {/* Switch to ref mode */}
                        {isInlineDataMode && (
                          <AppTooltip
                            title={`Link to existing damage. You'll link to a previously reported damage instead of creating a local copy.`}
                          >
                            <AppIconButton
                              size='small'
                              color='text'
                              onClick={() => {
                                const newDamageData: typeof itemDamage.damage = itemDamage.damage
                                  ? EntityHelper.switchEntityDataToRefMode(itemDamage.damage, null)
                                  : undefined;
                                const newItemDamage: typeof itemDamage = {
                                  damage: newDamageData,
                                  damageLocal: undefined,
                                };
                                setFieldValue(
                                  `items[${itemIndex}].damages[${itemDamageIndex}]`,
                                  newItemDamage,
                                );
                              }}
                            >
                              <AppIcon of='swapHoriz' />
                            </AppIconButton>
                          </AppTooltip>
                        )}

                        {/* View inline damage */}
                        {isInlineDataMode && itemDamage.damage && (
                          <AppTooltip title={`Quick view local copy`}>
                            <AppIconButton
                              size='small'
                              color='text'
                              onClick={() => {
                                setVehicleDamageDataToView(itemDamage.damage);
                              }}
                            >
                              <AppIcon of='quickView' />
                            </AppIconButton>
                          </AppTooltip>
                        )}

                        {/* Edit inline damage */}
                        {isInlineDataMode && itemDamage.damage && (
                          <AppTooltip title={`Edit local copy`}>
                            <AppIconButton
                              size='small'
                              color='text'
                              disabled={!formPerms.canEditDamages}
                              onClick={() => {
                                if (itemDamage.damage) {
                                  onUpdateInlineDamageData({
                                    item,
                                    itemDamage,
                                    damageData: itemDamage.damage,
                                  });
                                }
                              }}
                            >
                              <AppIcon of='editInline' />
                            </AppIconButton>
                          </AppTooltip>
                        )}

                        {/* Move damage to different item */}
                        {itemDamage.damage?.dataOriginMode && itemCount > 1 && (
                          <AppTooltip title='Move damage to different item'>
                            <AppDropdownIconButton
                              variant='default'
                              size='small'
                              color='text'
                              disabled={!formPerms.canEdit}
                              dropdownContent={
                                <MenuList sx={{ maxHeight: 400, overflow: "auto" }}>
                                  {values.items
                                    ?.map(
                                      (item2, i2) =>
                                        i2 !== itemIndex && (
                                          <MenuItem
                                            key={i2}
                                            onClick={() => {
                                              const movedDamage = itemDamage;
                                              setFieldValue(
                                                `items[${itemIndex}].damages`,
                                                ArrayHelper.removeByIndex(
                                                  [...(item.damages || [])],
                                                  itemDamageIndex,
                                                ),
                                              );
                                              setFieldValue(`items[${i2}].damages`, [
                                                ...(values.items?.at(i2)?.damages || []),
                                                movedDamage,
                                              ]);
                                            }}
                                          >
                                            <ListItemText>
                                              Move to #{i2 + 1} {item2.name}
                                            </ListItemText>
                                          </MenuItem>
                                        ),
                                    )
                                    .filter(Boolean)}
                                </MenuList>
                              }
                            >
                              <AppIcon of='moveItem' />
                            </AppDropdownIconButton>
                          </AppTooltip>
                        )}

                        {/* Remove damage */}
                        <AppTooltip title='Remove damage'>
                          <AppIconButton
                            size='small'
                            color='text'
                            disabled={!formPerms.canRemoveDamages}
                            onClick={() => {
                              setFieldValue(
                                `items[${itemIndex}].damages`,
                                ArrayHelper.removeByIndex(
                                  [...(item.damages || [])],
                                  itemDamageIndex,
                                ),
                              );
                              if (item.damages?.[itemDamageIndex]?.damage?.attachments?.length) {
                                // filtering attachments to delete attachments from deleted damage
                                const filteredAttachments =
                                  item.attachments?.filter(
                                    (attachment) =>
                                      !item.damages?.[itemDamageIndex]?.damage?.attachments?.some(
                                        (deletedAttachment) =>
                                          deletedAttachment?.id === attachment?.id,
                                      ),
                                  ) || [];
                                setFieldValue(
                                  `items[${itemIndex}].attachments`,
                                  filteredAttachments.length > 0 ? filteredAttachments : undefined,
                                );
                              }
                            }}
                          >
                            <AppIcon of='remove' />
                          </AppIconButton>
                        </AppTooltip>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>

              {ValidationHelper.getFormikErrorsAsString(
                getIn(errors, `items[${itemIndex}].damages`),
                {
                  isIncludeNested: false,
                },
              ) && (
                <FormHelperText error>
                  {ValidationHelper.getFormikErrorsAsString(
                    getIn(errors, `items[${itemIndex}].damages`),
                    {
                      isIncludeNested: false,
                    },
                  )}
                </FormHelperText>
              )}

              {/* Add damage */}
              <Box>
                <Button
                  disabled={!formPerms.canAddDamages}
                  variant='outlined'
                  size='small'
                  color='text'
                  startIcon={<AppIcon of='add' />}
                  onClick={() =>
                    setFieldValue(`items[${itemIndex}].damages`, [
                      ...(item.damages || []),
                      cast<DamageCostEvaluationItemValues>({
                        tempId: IdHelper.newId(),
                        damages: [{}],
                      }),
                    ])
                  }
                >
                  Add damage
                </Button>
              </Box>
            </Stack>

            {/* Repair spec  */}
            <Stack
              direction={{ xxs: "column", md: "row" }}
              spacing={1}
              sx={{ alignItems: { xxs: "flex-start", md: "center" } }}
            >
              <FormControl
                sx={{ flex: 10 }}
                margin='none'
                error={Boolean(
                  getIn(errors, `items[${itemIndex}].repairSpecId`) ||
                  getIn(errors, `items[${itemIndex}].repairSpec`),
                )}
              >
                <SelectOrCreateRepairSpec
                  autocompleteProps={{
                    debugKey: "RepairSpec",
                    entityId: item.repairSpec?.id,
                    disabled: !formPerms.canEditRepairSpec || item.isRepairSpecChanged,
                    required: true,
                    size: "small",
                    isPreload:
                      formInfo.isCreate &&
                      !!firstDamageData?.partTypeId &&
                      !!firstDamageData?.damageTypeId &&
                      !!vehicle,
                    isAutoSelectSingleOption: true,
                    searchFilters: {
                      match: repairSpecMatchQuery,
                    },
                    defaultCustomFilters: {
                      partTypeId: firstDamageData?.partTypeId || undefined,
                      damageTypeId: firstDamageData?.damageTypeId || undefined,
                      // vehicleSize: vehicle?.spec?.size,
                      bodyType: vehicle?.spec?.bodyType,
                      // fuelType: vehicle?.spec?.fuelType,
                      modelId: vehicle?.spec?.model?.id,
                      makeId: vehicle?.spec?.make?.id,
                      generationId: vehicle?.spec?.generation?.id,
                      modificationId: vehicle?.spec?.modification?.id,
                    },
                    textFieldProps: {
                      error: Boolean(
                        getIn(errors, `items[${itemIndex}].repairSpecId`) ||
                        getIn(errors, `items[${itemIndex}].repairSpec`),
                      ),
                      helperText: (
                        <>
                          {getIn(errors, `items[${itemIndex}].repairSpecId`) && (
                            <Box>
                              {ValidationHelper.getFormikErrorsAsString(
                                getIn(errors, `items[${itemIndex}].repairSpecId`),
                              )}
                            </Box>
                          )}
                          {getIn(errors, `items[${itemIndex}].repairSpec`) && (
                            <Box>
                              {ValidationHelper.getFormikErrorsAsString(
                                getIn(errors, `items[${itemIndex}].repairSpec`),
                              )}
                            </Box>
                          )}
                        </>
                      ),
                    },
                    onChange: (newValue) => {
                      setItemRepairSpec(newValue);
                      setFieldError(`items[${itemIndex}].repairSpecId`, undefined);
                      setFieldError(`items[${itemIndex}].repairSpec`, undefined);
                    },
                    onLoaded: (entities) => {
                      // when creating DCE with prefilled damages, auto-select the best matching repair spec for each DCE item
                      // NB: ensure best matching repair spec is auto-selected only initially

                      // Problem: setFieldValue experiences inconsistent behavior in the onLoaded method
                      // due to React's asynchronous state updates and batching. This can lead to stale
                      // or outdated values being applied, especially when multiple state changes occur simultaneously.

                      // Temporary solution: Wrapping setFieldValue in setTimeout ensures that the state
                      // updates occur after React's rendering process is completed, preventing issues
                      // with stale values. This approach is used as other solutions (like async/await)
                      // were found unreliable.
                      // timeoutRef.current = setTimeout(() => {
                      //   if (
                      //     formInfo.isCreate &&
                      //     isPrefillRepairSpec &&
                      //     !item.repairSpec &&
                      //     !item.isInitiallyAutoSelectedBestMatchingRepairSpec
                      //   ) {
                      //     const newValue = orderBy(
                      //       entities,
                      //       (x) => x.matchMeta?.score ?? 0,
                      //       "desc",
                      //     ).at(0);

                      //     if (newValue) {
                      //       setItemRepairSpec(newValue);
                      //       setFieldValue(
                      //         `items[${itemIndex}].isInitiallyAutoSelectedBestMatchingRepairSpec`,
                      //         true,
                      //       );
                      //     }
                      //   }
                      // }, 3000);
                      setLoadedEntities(entities);
                    },
                  }}
                  createUpdateProps={{
                    defaultValues: {
                      damageTypeId: item.damages?.at(0)?.damage?.damageTypeId || undefined,
                      partTypeId: item.damages?.at(0)?.damage?.partTypeId || undefined,
                      currency: currency,
                    },
                  }}
                  createFormPlacement='modal'
                  onCreate={(newValue) => {
                    setItemRepairSpec(newValue);
                  }}
                />
              </FormControl>

              <AuthorizedElement
                permissions={[
                  AppPermission.FleetAppAccess,
                  AppPermission.RepairCatalogRead,
                  AppPermission.RepairCatalogManage,
                ]}
              >
                {!item.isRepairSpecChanged && formPerms.canEditRepairSpec && (
                  <AppTooltip title='Edit local copy of the repair spec'>
                    <Button
                      variant='outlined'
                      color='text'
                      size='small'
                      disabled={!formPerms.canEdit}
                      startIcon={<AppIcon of='edit' />}
                      onClick={() => {
                        setFieldValue(`items[${itemIndex}].isRepairSpecChanged`, true);
                        if (!item.repairSpec) {
                          setFieldValue(`items[${itemIndex}].repairSpec`, {});
                        }
                      }}
                    >
                      Edit as local copy
                    </Button>
                  </AppTooltip>
                )}

                {item.isRepairSpecChanged && formPerms.canEditRepairSpec && (
                  <Button
                    variant='outlined'
                    color='text'
                    size='small'
                    disabled={!formPerms.canEdit}
                    startIcon={<AppIcon of='selectFromList' />}
                    onClick={() => {
                      setFieldValue(`items[${itemIndex}].isRepairSpecChanged`, false);
                      setFieldValue(`items[${itemIndex}].repairSpec`, undefined);
                    }}
                  >
                    Select existing spec
                  </Button>
                )}

                {/* Create repair spec from spec data */}
                {item.isRepairSpecChanged && formPerms.canEditRepairSpec && (
                  <AppTooltip title='Create new repair spec based on the local evaluation data '>
                    <Button
                      size='small'
                      color='text'
                      variant='outlined'
                      disabled={!formPerms.canEdit}
                      startIcon={<AppIcon of='add' />}
                      onClick={() => {
                        // Business requirement: when creating a new repair spec from an existing one, detalization must be updated based on the current vehicle data.
                        const updatedSpec = {
                          ...item.repairSpec,
                          detalization: {
                            ...item.repairSpec?.detalization,
                            makeId: values.vehicle?.spec?.make?.id,
                            modelId: values.vehicle?.spec?.model?.id,
                            generationId: values.vehicle?.spec?.generation?.id,
                            modificationId: values.vehicle?.spec?.modification?.id,
                            bodyType: values.vehicle?.spec?.bodyType,
                          },
                        };
                        setCreateRepairSpecFromSpecData(updatedSpec);
                        setIsCreateRepairSpecModalOpen(true);
                      }}
                    >
                      Save as new spec
                    </Button>
                  </AppTooltip>
                )}
              </AuthorizedElement>
            </Stack>
          </Box>

          {/* Selected repair spec */}
          {item.repairSpec && (
            <Box>
              <FoldableBlock
                defaultIsFolded={false}
                trigger={{
                  label: <Typography variant='h4'>Repair spec</Typography>,
                }}
              >
                <Stack spacing={1}>
                  {/* Repair spec details with create/edit */}
                  <AuthorizedElement
                    permissions={[
                      AppPermission.FleetAppAccess,
                      AppPermission.RepairCatalogRead,
                      AppPermission.RepairCatalogManage,
                    ]}
                  >
                    <FoldableBlock
                      defaultIsFolded={true}
                      trigger={{
                        label: (
                          <Typography component='span' variant='h6'>
                            Details
                          </Typography>
                        ),
                      }}
                    >
                      <RepairSpecDataInputDtoFormInputs
                        disabled={!formPerms.canEdit || !item.isRepairSpecChanged}
                        mode={formInfo.isUpdate ? "update" : "create"}
                        repairSpecId={undefined}
                        values={item.repairSpec || undefined}
                        defaultValues={{
                          currency: currency,
                          vehicleType: vehicle?.spec?.type || VehicleType.Car,
                          partTypeId: item.damages?.at(0)?.damage?.partTypeId,
                          damageTypeId: item.damages?.at(0)?.damage?.damageTypeId,
                          detalization: {
                            type: RepairSpecDetalizationType.General,
                          },
                          items: [],
                          customName: item.name,
                        }}
                        // formikProps={{
                        //   errors: getIn(
                        //     errors,
                        //     `items[${itemIndex}].repairSpec`,
                        //   ),
                        //   touched: getIn(
                        //     touched,
                        //     `items[${itemIndex}].repairSpec`,
                        //   ),
                        // }}
                        formikProps={FormikHelper.getSubProps(
                          formikProps,
                          `items[${itemIndex}].repairSpec`,
                          (x) => x.items?.at(itemIndex)?.repairSpec,
                        )}
                        displayProps={{
                          mainInputs: false,
                          items: true,
                          itemsHeader: false,
                          itemInputs: true,
                          lineItemsSummary: false,
                        }}
                        withGeneralValidationError
                        onChange={(newValue) => {
                          setFieldValue(`items[${itemIndex}].repairSpec`, newValue || undefined);
                          setItemNameAuto(newValue?.name);
                        }}
                      />
                    </FoldableBlock>
                  </AuthorizedElement>

                  {/* Repair spec summary */}
                  {/* <FoldableBlock
                  defaultIsFolded={false}
                  trigger={{
                    label: <Typography variant='h6'>Summary</Typography>,
                  }}
                >
                  <Box>
                    <GeneralPriceSummaryDisplay
                      direction='row'
                      summary={{
                        currency: item.repairSpec?.currency,
                        subTotal: item.repairSpec?.subTotal,
                        discount: item?.repairSpec?.discount || undefined,
                        tax: item?.repairSpec?.tax || undefined,
                        total: item.repairSpec?.total || 0,
                      }}
                    />
                  </Box>
                </FoldableBlock> */}
                  <GeneralPriceSummaryDisplay
                    direction={isMobile ? "column" : "row"}
                    summary={{
                      currency: item.repairSpec?.currency,
                      subTotal: item.repairSpec?.subTotal,
                      discount: item?.repairSpec?.discount || undefined,
                      tax: item?.repairSpec?.tax || undefined,
                      total: item.repairSpec?.total || 0,
                    }}
                  />
                </Stack>
              </FoldableBlock>
            </Box>
          )}

          {/* Notes, attachments */}
          <Box>
            {!isNil(item.notes) && (
              <AppTextArea
                error={Boolean(getIn(errors, `items[${itemIndex}].notes`))}
                fullWidth
                helperText={getIn(errors, `items[${itemIndex}].notes`)}
                mode='notes'
                margin='dense'
                name={`items[${itemIndex}].notes`}
                onBlur={handleBlur}
                onChange={handleChange}
                value={item.notes}
                variant='outlined'
              />
            )}

            {!isNil(item.attachments) && (
              <FormControl fullWidth margin='dense'>
                <FileUploader
                  multiple
                  defaultFiles={
                    // Why this logic? defaultFiles actually act as files and not default.
                    // formInfo.isCreate
                    //   ? item.attachments
                    //     ? FileItem.createManyFrom(item.attachments)
                    //     : undefined
                    //   : item.initialAttachments
                    //     ? FileItem.createManyFrom(item.initialAttachments)
                    //     : undefined
                    FileItem.createManyFrom(item.attachments)
                  }
                  onChange={(newFiles) => {
                    setFieldValue(
                      `items[${itemIndex}].attachments`,
                      FileItem.toManyGeneralAttachmentInputDto(newFiles),
                    );
                  }}
                  onUploadStarted={() => {
                    setFieldValue("isAttachmentsUploading", true);
                  }}
                  onUploadFinished={() => {
                    setFieldValue("isAttachmentsUploading", false);
                  }}
                  onValidationStatusChange={(filesValidationStatus) => {
                    if (filesValidationStatus)
                      setFieldValue(
                        `items[${itemIndex}].isAttachmentsHaveErrors`,
                        Object.values(filesValidationStatus).some((x) => x === false),
                      );
                  }}
                />
              </FormControl>
            )}
          </Box>
        </Stack>

        {/* Create repair spec from local repair spec data */}
        {createRepairSpecFromSpecData && (
          <RepairSpecCreateUpdateModal
            open={isCreateRepairSpecModalOpen}
            onClose={() => {
              setIsCreateRepairSpecModalOpen(false);
              setCreateRepairSpecFromSpecData(undefined);
            }}
            createUpdateProps={{
              defaultValues: {
                data: createRepairSpecFromSpecData,
              },
              onSave: () => {
                setIsCreateRepairSpecModalOpen(false);
                setCreateRepairSpecFromSpecData(undefined);
              },
            }}
          />
        )}

        {/* Vehicle damage data view modal */}
        {vehicleDamageDataToView && (
          <VehicleDamageDataViewModal
            open={!!vehicleDamageDataToView}
            onClose={() => {
              setVehicleDamageDataToView(null);
            }}
            viewProps={{
              entityData: vehicleDamageDataToView,
            }}
          />
        )}
      </Box>
    );
  },
);
